<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { useNotification } from "@ui-kit";
  import { useBarcode, useReadRfid } from "@plugins";
  import { ScanScreen, ScanScreenStart } from "@src/components";
  import { WTFIRInputType, useGetWTFIR, useLazyProductsLoad, useCheckSales, WTFIRTypeData } from "@src/api";
  import { default as SalesItemList } from "./CheckSalesItemList.vue";
  import { default as SalesItem } from "./CheckSalesItem.vue";

  const emit = defineEmits<{ (e: "close"): void }>();

  const barcode = useBarcode();
  const readRfid = useReadRfid();
  const { t } = useI18n();
  const { show, clear } = useNotification();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: checkSales } = useCheckSales();
  const { execute: getProduct } = useLazyProductsLoad();

  const itemList = ref<
    {
      id: string;
      article: string;
      img: string;
      isSold: boolean;
      dateSales: string | null;
      cis?: string;
      epc?: string;
    }[]
  >([]);
  const isInitialLoad = ref(true);

  const handleScanData = async (data: string, type: WTFIRInputType) => {
    clear();

    const resultWTFIR = await getWTFIR({ data, input_type: type });

    if (resultWTFIR?.isError.value) return;
    if (![WTFIRTypeData.cis, WTFIRTypeData.epc].includes(resultWTFIR.data.value.type_data)) {
      show({ title: t("Не правильный КИЗ/EPC"), type: "error" });

      return;
    }

    const productId = resultWTFIR.data.value.items?.at(0) || null;

    if (!productId) {
      show({ title: t("Товар не найден"), type: "error" });

      return;
    }

    const productResult = await getProduct({ item_id: [productId] });
    const checkSalesResult = await checkSales({
      ...(resultWTFIR.data.value.type_data === WTFIRTypeData.epc ? { epc: data } : {}),
      ...(resultWTFIR.data.value.type_data === WTFIRTypeData.cis ? { cis: data } : {}),
    });

    if (productResult.isError.value || checkSalesResult.isError.value) {
      show({ title: t("Ошибка получения товара"), type: "error" });

      return;
    }

    const existsItem = itemList.value.find((item) => item.id === productId) || {};

    if (Object.keys(existsItem).length > 0) {
      itemList.value = itemList.value.filter((item) => item.id !== productId);
    }

    const item = {
      ...existsItem,
      id: productId,
      article: productResult.data.value.items.at(0).article,
      img: productResult.data.value.items.at(0).image_url,
      isSold: checkSalesResult.data.value.sales,
      dateSales: checkSalesResult.data.value.date_sales,
      ...(resultWTFIR.data.value.type_data === WTFIRTypeData.epc ? { epc: data } : {}),
      ...(resultWTFIR.data.value.type_data === WTFIRTypeData.cis ? { cis: data } : {}),
    };

    itemList.value.unshift(item);

    if (itemList.value.length > 4) {
      itemList.value.pop();
    }

    isInitialLoad.value = false;
  };

  onMounted(() => {
    barcode.subscribe((data: string) => {
      handleScanData(data, WTFIRInputType.barcode);
    });
    readRfid.subscribe((data) => {
      handleScanData(data.value, WTFIRInputType.rfid);
    });
  });

  onUnmounted(() => {
    barcode.unsubscribe();
    readRfid.unsubscribe();
  });
</script>

<template>
  <div class="widget__check-sales" data-testid="widget-check-sales">
    <ScanScreen
      v-if="isInitialLoad"
      data-testid="product-fitting"
      @close="emit('close')"
      :modal="{ title: $t('Проверка продаж на КСО'), subtitle: $t('Отсканируйте КИЗ или ECP') }"
    >
      <ScanScreenStart />
    </ScanScreen>
    <SalesItemList v-else @click:back="emit('close')" @click:clear="itemList = []">
      <SalesItem
        v-for="(item, index) in itemList"
        :key="item.id + '-' + index"
        :isSold="item.isSold"
        :hasCis="!!item.cis"
        :hasEpc="!!item.epc"
        :dateSales="item.dateSales"
        :img="item.img"
        :article="item.article"
        data-testid="sales-item"
      />
    </SalesItemList>
  </div>
</template>

<style scoped>
  .widget__check-sales {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
  }
</style>
